import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Award, Users, Star, Globe, Plus, Check, Loader2, Lock } from 'lucide-react';
import { formatPrice, formatDurationHours, truncateText } from '../../lib/utils';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${API_BASE}/api`;

export const CourseCard = ({ course, showPrice = true, isInPathway = false, locked = false }) => {
  const { token, isAuthenticated } = useAuth();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [addingToPathway, setAddingToPathway] = useState(false);
  const [addedToPathway, setAddedToPathway] = useState(isInPathway);
  
  const {
    course_id,
    slug,
    title,
    description,
    price,
    currency = 'ZAR',
    duration_hours,
    cpd_points,
    category,
    level,
    thumbnail_url,
    instructor_name,
    content_type,
    aicc_url,
    iframe_url,
  } = course;

  // Use slug for SEO-friendly URLs, fallback to course_id
  // Handle null, undefined, and empty string cases
  const courseUrl = (slug && slug.trim()) ? slug : course_id;

  // Check if course is external (AICC/iframe-based)
  const isExternal = content_type === 'aicc' || content_type === 'external' || aicc_url || iframe_url;

  const defaultThumbnail = "https://images.unsplash.com/photo-1705234384669-c6d31c61b789?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxsZWFkZXJzaGlwJTIwYnVzaW5lc3MlMjBjb3Vyc2V8ZW58MHx8fHwxNzc0NDQ4NjUxfDA&ixlib=rb-4.1.0&q=85";
  
  // Convert /api/files/ paths to public URLs for display without authentication
  const getImageUrl = (url) => {
    if (!url) return defaultThumbnail;
    if (url.startsWith('/api/files/')) {
      // Convert to public endpoint that doesn't require auth
      return `${API_BASE}${url.replace('/api/files/', '/api/public/files/')}`;
    }
    return url;
  };
  
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  
  const handleAddToCompetencyPathway = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to add courses to your competency pathway');
      return;
    }
    
    setAddingToPathway(true);
    try {
      const response = await axios.post(`${API}/competencies/my-learning-pathway/add`, {
        item_type: 'course',
        item_id: course_id,
        item_title: title
      }, { headers });
      
      if (response.data.already_added) {
        toast.info('Already in your Competency Learning Pathway');
      } else {
        toast.success('Added to Competency Learning Pathway!');
      }
      setAddedToPathway(true);
    } catch (error) {
      console.error('Failed to add to pathway:', error);
      toast.error('Failed to add to pathway');
    } finally {
      setAddingToPathway(false);
    }
  };

  return (
    <Link 
      to={`/courses/${courseUrl}`}
      className="group relative bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden hover:border-navigate-gold/50 transition-all duration-300 hover:-translate-y-1 block"
      data-testid={`course-card-${course_id}`}
    >
      {/* Image */}
      <div className="aspect-video w-full overflow-hidden bg-zinc-800 relative">
        {/* Skeleton placeholder */}
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 bg-zinc-800 animate-pulse" />
        )}
        <img 
          src={getImageUrl(thumbnail_url)}
          alt={title}
          loading="lazy"
          decoding="async"
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            setImageError(true);
            setImageLoaded(true);
            e.target.src = defaultThumbnail;
            e.target.onerror = null;
          }}
        />
        <div className="absolute inset-0 course-card-overlay" />
        
        {/* External Course Badge */}
        {isExternal && (
          <div className="absolute top-3 left-3 bg-cyan-500/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-medium text-white flex items-center gap-1.5 shadow-lg">
            <Globe className="h-3 w-3" />
            External
          </div>
        )}
        
        {/* Locked overlay for learners without an active enrollment */}
        {locked && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px] flex flex-col items-center justify-center z-10 px-4 text-center">
            <Lock className="h-7 w-7 text-amber-400 mb-2" />
            <span className="text-xs text-zinc-200 font-medium leading-snug">
              Access denied. Please contact your admin for access.
            </span>
          </div>
        )}

        {/* Duration Badge */}
        <div className="absolute top-3 right-3 duration-badge px-2 py-1 rounded text-xs font-medium text-white flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatDurationHours(duration_hours) || '30 mins'}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Category & Level */}
        <div className="flex items-center gap-2 mb-2">
          <span className="tag text-xs">{category}</span>
          <span className="text-xs text-zinc-500 capitalize">{level}</span>
        </div>

        {/* Title */}
        <h3 className="font-heading font-semibold text-white text-lg mb-2 group-hover:text-navigate-gold transition-colors line-clamp-2">
          {title}
        </h3>

        {/* Description */}
        <p className="text-sm text-zinc-400 mb-4 line-clamp-2">
          {truncateText(description, 100)}
        </p>

        {/* Instructor */}
        {instructor_name && (
          <p className="text-xs text-zinc-500 mb-4">
            By {instructor_name}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
          {showPrice && (
            <span className="price-tag text-lg">
              {price > 0 ? formatPrice(price, currency) : 'Free'}
            </span>
          )}
          
          <div className="flex items-center gap-2">
            {/* Add to Competency Pathway Button (hidden when locked) */}
            {isAuthenticated && !locked && (
              addedToPathway ? (
                <div
                  className="p-1.5 rounded-lg bg-teal-500/20 text-teal-400 cursor-default"
                  title="Added to Competency Pathway"
                >
                  <Check className="h-4 w-4" />
                </div>
              ) : (
                <button
                  onClick={handleAddToCompetencyPathway}
                  disabled={addingToPathway}
                  className="p-1.5 rounded-lg bg-zinc-800 hover:bg-teal-500/20 text-zinc-400 hover:text-teal-400 transition-colors disabled:opacity-50"
                  title="Add to Competency Pathway"
                >
                  {addingToPathway ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </button>
              )
            )}

            {locked ? (
              <span className="text-sm text-amber-400 font-medium flex items-center gap-1">
                <Lock className="h-3.5 w-3.5" /> Locked
              </span>
            ) : (
              <span className="text-sm text-navigate-gold font-medium group-hover:translate-x-1 transition-transform">
                View →
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CourseCard;
